import React, { useEffect, useRef, useState } from "react";
import HTMLFlipBook from "react-pageflip";
import { ChevronLeft, ChevronRight, UtensilsCrossed, Wine } from "lucide-react";

const MenuFlipbook = () => {
  // =========================================================
  // ACTIVE MENU
  // =========================================================
  const [activeMenu, setActiveMenu] = useState("food");

  // =========================================================
  // SCREEN SIZE
  // =========================================================
  const [screenSize, setScreenSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  // =========================================================
  // FLIPBOOK REFERENCES
  // =========================================================
  const foodBookRef = useRef(null);
  const drinkBookRef = useRef(null);

  // =========================================================
  // UPDATE SCREEN SIZE
  // =========================================================
  useEffect(() => {
    const handleResize = () => {
      setScreenSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // =========================================================
  // CALCULATE RESPONSIVE BOOK SIZE
  // =========================================================

  const getBookSize = () => {
    const width = screenSize.width;
    const height = screenSize.height;

    // ============================================
    // MOBILE
    // ============================================
    if (width < 640) {
      const availableWidth = width - 40;
      const availableHeight = height - 250;

      const bookWidth = Math.min(availableWidth, availableHeight * 0.72);

      const bookHeight = bookWidth / 0.72;

      return {
        width: Math.max(260, Math.floor(bookWidth)),
        height: Math.max(380, Math.floor(bookHeight)),
        portrait: true,
      };
    }

    // ============================================
    // TABLET
    // ============================================
    if (width >= 640 && width < 1024) {
      const availableWidth = width - 120;
      const availableHeight = height - 230;

      const bookWidth = Math.min(availableWidth, availableHeight * 0.72);

      const bookHeight = bookWidth / 0.72;

      return {
        width: Math.max(400, Math.floor(bookWidth)),
        height: Math.max(560, Math.floor(bookHeight)),
        portrait: true,
      };
    }

    // ============================================
    // DESKTOP
    // ============================================
    if (width >= 1024 && width < 1536) {
      const availableWidth = width - 180;
      const availableHeight = height - 220;

      const bookWidth = Math.min(availableWidth, availableHeight * 0.72);

      const bookHeight = bookWidth / 0.72;

      return {
        width: Math.max(500, Math.floor(bookWidth)),
        height: Math.max(650, Math.floor(bookHeight)),
        portrait: true,
      };
    }

    // ============================================
    // LARGE DESKTOP
    // ============================================
    const availableWidth = width - 240;
    const availableHeight = height - 220;

    const bookWidth = Math.min(availableWidth, availableHeight * 0.72);

    const bookHeight = bookWidth / 0.72;

    return {
      width: Math.min(650, Math.floor(bookWidth)),
      height: Math.min(900, Math.floor(bookHeight)),
      portrait: true,
    };
  };

  const bookSize = getBookSize();

  // =========================================================
  // NEXT PAGE
  // =========================================================
  const nextPage = () => {
    if (activeMenu === "food") {
      foodBookRef.current?.pageFlip().flipNext();
    } else {
      drinkBookRef.current?.pageFlip().flipNext();
    }
  };

  // =========================================================
  // PREVIOUS PAGE
  // =========================================================
  const previousPage = () => {
    if (activeMenu === "food") {
      foodBookRef.current?.pageFlip().flipPrev();
    } else {
      drinkBookRef.current?.pageFlip().flipPrev();
    }
  };

  // =========================================================
  // CHANGE MENU
  // =========================================================
  const changeMenu = (menu) => {
    setActiveMenu(menu);
  };

  // =========================================================
  // COMMON FLIPBOOK SETTINGS
  // =========================================================
  const flipBookSettings = {
    width: bookSize.width,
    height: bookSize.height,

    size: "fixed",

    minWidth: 260,
    maxWidth: 650,

    minHeight: 380,
    maxHeight: 900,

    showCover: true,

    // Portrait is best for mobile/tablet reading
    usePortrait: bookSize.portrait,

    drawShadow: true,
    maxShadowOpacity: 0.55,

    flippingTime: 900,

    useMouseEvents: true,
    clickEventForward: true,

    mobileScrollSupport: true,

    swipeDistance: 20,

    startPage: 0,

    className: "menu-flipbook",
  };

  return (
    <section className="relative min-h-[100svh] w-full overflow-hidden bg-[#000000]">
      {/* =====================================================
          HEADER
      ===================================================== */}
      <div className="relative z-30 px-4 pt-5 text-center sm:pt-7 md:pt-8">
        <p className="mb-1 text-[9px] font-medium uppercase tracking-[0.45em] text-white/40 sm:text-[10px] md:text-xs">
          Capeesh
        </p>

        <h1 className="font-serif text-2xl font-light tracking-tight text-white sm:text-3xl md:text-4xl lg:text-5xl">
          {activeMenu === "food" ? "FOOD MENU" : "DRINK MENU"}
        </h1>
      </div>

      {/* =====================================================
          FOOD / DRINK SWITCH
      ===================================================== */}
      <div className="relative z-40 mt-4 flex justify-center px-3 sm:mt-5 md:mt-6">
        <div className="flex rounded-full border border-white/10 bg-[#171717] p-1 shadow-2xl">
          {/* FOOD BUTTON */}
          <button
            onClick={() => changeMenu("food")}
            className={`
              flex items-center justify-center gap-1.5
              rounded-full
              px-4 py-2.5
              text-[10px]
              font-medium
              uppercase
              tracking-wider
              transition-all
              duration-300

              sm:gap-2
              sm:px-6
              sm:py-3
              sm:text-xs

              md:px-8
              md:py-3.5
              md:text-sm

              ${
                activeMenu === "food"
                  ? "bg-white text-black shadow-lg"
                  : "text-white/50 hover:text-white"
              }
            `}
          >
            <UtensilsCrossed size={15} strokeWidth={1.7} />

            <span>Food Menu</span>
          </button>

          {/* DRINK BUTTON */}
          <button
            onClick={() => changeMenu("drink")}
            className={`
              flex items-center justify-center gap-1.5
              rounded-full
              px-4 py-2.5
              text-[10px]
              font-medium
              uppercase
              tracking-wider
              transition-all
              duration-300

              sm:gap-2
              sm:px-6
              sm:py-3
              sm:text-xs

              md:px-8
              md:py-3.5
              md:text-sm

              ${
                activeMenu === "drink"
                  ? "bg-white text-black shadow-lg"
                  : "text-white/50 hover:text-white"
              }
            `}
          >
            <Wine size={15} strokeWidth={1.7} />

            <span>Drink Menu</span>
          </button>
        </div>
      </div>

      {/* =====================================================
          FLIPBOOK AREA
      ===================================================== */}
      <div
        className="
          relative
          flex
          w-full
          items-center
          justify-center
          px-2
          pb-8
          pt-5

          sm:px-6
          sm:pt-6

          md:px-10
          md:pt-8

          lg:px-16
        "
      >
        {/* ===================================================
            FOOD MENU
        =================================================== */}
        {activeMenu === "food" && (
          <HTMLFlipBook
            key={`food-${bookSize.width}-${bookSize.height}`}
            ref={foodBookRef}
            {...flipBookSettings}
          >
            {/* FOOD COVER */}
            <div className="h-full w-full overflow-hidden bg-white">
              <img
                src="/menu-cover1.jpg"
                alt="Food Menu Cover"
                className="block h-full w-full object-cover"
              />
            </div>

            {/* FOOD PAGE 1 */}
            <div className="h-full w-full overflow-hidden bg-white">
              <img
                src="/menu-page-1.jpg"
                alt="Food Menu Page 1"
                className="block h-full w-full object-cover"
              />
            </div>

            {/* FOOD PAGE 2 */}
            <div className="h-full w-full overflow-hidden bg-white">
              <img
                src="/menu-page-2.jpg"
                alt="Food Menu Page 2"
                className="block h-full w-full object-cover"
              />
            </div>

            {/* FOOD BACK */}
            <div className="h-full w-full overflow-hidden bg-white">
              <img
                src="/menu-back1.jpg"
                alt="Food Menu Back Cover"
                className="block h-full w-full object-cover"
              />
            </div>
          </HTMLFlipBook>
        )}

        {/* ===================================================
            DRINK MENU
        =================================================== */}
        {activeMenu === "drink" && (
          <HTMLFlipBook
            key={`drink-${bookSize.width}-${bookSize.height}`}
            ref={drinkBookRef}
            {...flipBookSettings}
          >
            {/* DRINK COVER */}
            <div className="h-full w-full overflow-hidden bg-white">
              <img
                src="/drink-cover.jpg"
                alt="Drink Menu Cover"
                className="block h-full w-full object-cover"
              />
            </div>

            {/* DRINK PAGE 1 */}
            <div className="h-full w-full overflow-hidden bg-white">
              <img
                src="/drink-page-1.jpg"
                alt="Drink Menu Page 1"
                className="block h-full w-full object-cover"
              />
            </div>

            {/* DRINK PAGE 2 */}
            <div className="h-full w-full overflow-hidden bg-white">
              <img
                src="/drink-page-2.jpg"
                alt="Drink Menu Page 2"
                className="block h-full w-full object-cover"
              />
            </div>

            {/* DRINK BACK */}
            <div className="h-full w-full overflow-hidden bg-white">
              <img
                src="/drink-back.jpg"
                alt="Drink Menu Back Cover"
                className="block h-full w-full object-cover"
              />
            </div>
          </HTMLFlipBook>
        )}
      </div>

      {/* =====================================================
          LEFT / PREVIOUS BUTTON
      ===================================================== */}
      <button
        onClick={previousPage}
        aria-label="Previous page"
        className="
          fixed
          left-2
          top-1/2
          z-50
          flex
          h-10
          w-10
          -translate-y-1/2
          items-center
          justify-center
          rounded-full
          border
          border-white/20
          bg-black/80
          text-white
          shadow-xl
          backdrop-blur-md
          transition-all
          duration-300

          hover:scale-110
          hover:bg-white
          hover:text-black

          sm:left-4
          sm:h-12
          sm:w-12

          md:left-7
          md:h-14
          md:w-14

          lg:left-10
          lg:h-16
          lg:w-16
        "
      >
        <ChevronLeft size={24} strokeWidth={1.5} />
      </button>

      {/* =====================================================
          RIGHT / NEXT BUTTON
      ===================================================== */}
      <button
        onClick={nextPage}
        aria-label="Next page"
        className="
          fixed
          right-2
          top-1/2
          z-50
          flex
          h-10
          w-10
          -translate-y-1/2
          items-center
          justify-center
          rounded-full
          border
          border-white/20
          bg-black/80
          text-white
          shadow-xl
          backdrop-blur-md
          transition-all
          duration-300
          hover:scale-110
          hover:bg-white
          hover:text-black
          sm:right-4
          sm:h-12
          sm:w-12
          md:right-7
          md:h-14
          md:w-14
          lg:right-10
          lg:h-16
          lg:w-16
        "
      >
        <ChevronRight size={24} strokeWidth={1.5} />
      </button>
    </section>
  );
};

export default MenuFlipbook;
